import { Label, ListBox, Select } from "@heroui/react";
import { isAxiosError } from "axios";
import { useMemo, useState } from "react";

import { FormModal, notify } from "@/components/kit";
import { useGetAllCategories } from "@/hooks/api/category/useGetAllCategories";
import { useUpdateSubCategoryParentCategories } from "@/hooks/api/category/useUpdateSubCategoryParentCategories";

interface ManageParentCategoriesModalProps {
  subCategoryId: string;
  subCategoryName: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function ManageParentCategoriesModal({
  subCategoryId,
  subCategoryName,
  isOpen,
  onClose,
  onSuccess,
}: ManageParentCategoriesModalProps) {
  const { data: categories, isLoading: isCategoriesLoading } =
    useGetAllCategories();
  const { mutate: updateParentCategories, isPending } =
    useUpdateSubCategoryParentCategories();

  // Derive current parent categories from the categories list
  const currentParentIds = useMemo(() => {
    if (!categories) return [];

    return categories
      .filter((cat) =>
        cat.subCategories.some((sc) => sc.id === subCategoryId),
      )
      .map((cat) => cat.id);
  }, [categories, subCategoryId]);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Sync selectedIds when modal opens with new data
  const [prevSubCategoryId, setPrevSubCategoryId] = useState("");

  if (subCategoryId !== prevSubCategoryId && currentParentIds.length > 0) {
    setPrevSubCategoryId(subCategoryId);
    setSelectedIds(currentParentIds);
  }

  const handleSave = () => {
    if (selectedIds.length === 0) {
      notify.error("Please select at least one parent category");

      return;
    }

    updateParentCategories(
      {
        subCategoryId,
        data: { categoryIds: selectedIds },
      },
      {
        onSuccess: () => {
          notify.success("Parent categories updated successfully");
          onSuccess();
        },
        onError: (error) => {
          notify.error(
            "Failed to update parent categories",
            isAxiosError(error)
              ? (error.response?.data?.message ?? "Unknown error")
              : "Unknown error",
          );
        },
      },
    );
  };

  return (
    <FormModal
      isOpen={isOpen}
      isSubmitDisabled={selectedIds.length === 0}
      isSubmitting={isPending}
      submitLabel="Save"
      title="Manage Parent Categories"
      onClose={onClose}
      onSubmit={handleSave}
    >
      <p className="text-muted -mt-2 text-sm">
        Select which categories &ldquo;{subCategoryName}&rdquo; belongs to
      </p>
      <Select
        isRequired
        isDisabled={isCategoriesLoading}
        placeholder="Select parent categories"
        selectionMode="multiple"
        value={selectedIds}
        variant="secondary"
        onChange={(keys) => setSelectedIds((keys ?? []).map(String))}
      >
        <Label>Parent Categories</Label>
        <Select.Trigger>
          <Select.Value />
          <Select.Indicator />
        </Select.Trigger>
        <Select.Popover>
          <ListBox selectionMode="multiple">
            {(categories ?? []).map((category) => (
              <ListBox.Item
                key={category.id}
                id={category.id}
                textValue={category.name}
              >
                {category.name}
                <ListBox.ItemIndicator />
              </ListBox.Item>
            ))}
          </ListBox>
        </Select.Popover>
      </Select>
    </FormModal>
  );
}
